import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAgent } from "@/hooks/use-agent";
import { useSession } from "@/hooks/useSession";
import { useSessionHistory } from "@/hooks/use-session-history";
import { useAuth } from "@/hooks/useAuth";
import { ChatResponse, ChatEntry, Suggestion } from "@/types/chat";
import { UserSession } from "@/types/session";
import { getHubInitialMessage, isPrimaryChatEntry } from "@/config/chat.config";
import { getCoachingInitialAssistantMessage } from "@/config/coachingIntro";
import { teamsCoachingSeedAssistantHtml } from "@/lib/myTeamCoachingSeed";
import type { MentorNavigateState } from "@/types/mentorNavigation";
import { ContentCard } from "@/components/ui/ContentCard";
import { resolveMessagesFromHistory } from "./historyConvertor";
import {
  SessionInfo,
  AIMentorPageActionsFooter,
  ChatHistory,
  ChatInput,
  ChatQuickAnswers,
  HubHero,
} from "./components";
import { useAnalytics } from "@/lib/posthog";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { readChatTextAttachment } from "@/lib/chatAttachmentText";
import { extractAssistantMessageText } from "@/lib/chatWebhookResponse";
import { toAssistantBubbleHtml } from "@/lib/streamDisplayText";
import {
  matchRecommendationFromUserMessage,
  resolveChatRecommendation,
  type ResolvedRecommendation,
} from "@/lib/chatRecommendationMatcher";
import { deriveWorkspacePersona } from "@/config/workspacePersona";
import { ChatRecommendationCard } from "@/components/chat/ChatRecommendationCard";
import { useSeenRecommendations } from "@/hooks/useSeenRecommendations";
import { resolveQuickAnswerMessage } from "@/lib/suggestionMessage";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

const DAILY_RESET_POLLING_INTERVAL = 8000;
const HISTORY_REFRESH_LIMIT = 40;

export const TextAgentPage = ({
  toggleInputMode,
  chatEntry,
  isVoiceModeAvailable,
  hubLayout = false,
  suppressInlineVoice = false,
  hubHero = false,
  hubGreetingLine,
}: {
  toggleInputMode?: () => void;
  chatEntry: ChatEntry;
  isVoiceModeAvailable?: boolean;
  /** Home hub: attachment +, hide footer/session strip, optional empty start. */
  hubLayout?: boolean;
  /** When true with hub, voice control is shown beside the chat (not in the input row). */
  suppressInlineVoice?: boolean;
  /** Hub "New Chat" empty-state: render the centered hero (greeting + input + chips) instead of a chat card. */
  hubHero?: boolean;
  /** Greeting rendered inside the hub hero (e.g. "Evening, Sam."). */
  hubGreetingLine?: string;
}) => {
  const location = useLocation();
  const mentorState = location.state as MentorNavigateState | null | undefined;
  const teamsCoachingSeed = mentorState?.teamsCoachingSeed;
  const mentorDraftMessage = mentorState?.mentorDraftMessage?.trim();

  const { toast } = useToast();
  const { userData } = useAuth();
  const { createNewSession, sessions, isLoading: isLoadingSession, getSession, loadClosedSessions } =
    useSession();
  const currentEntrySession = sessions?.[chatEntry];

  const { sendMessage, sendMessageStream, isSendingMessage } = useAgent();
  const { trackChatSessionStart, trackChatSessionEnd } = useAnalytics();

  const [isDailyResetPolling, setIsDailyResetPolling] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const messagesRef = useRef<ChatMessage[]>(messages);
  messagesRef.current = messages;
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [recommendation, setRecommendation] = useState<ResolvedRecommendation | null>(null);
  const [organizationGuidanceStatus, setOrganizationGuidanceStatus] = useState<string | null>(null);
  const sessionStartTime = useRef<number | null>(null);
  const inputDraftRef = useRef("");
  const [inputDraftClearKey, setInputDraftClearKey] = useState(0);

  const persona = deriveWorkspacePersona(userData);
  const { seenIds, markSeen } = useSeenRecommendations(userData?.user_id);

  const refreshConversationHistory = useCallback(() => {
    void loadClosedSessions(HISTORY_REFRESH_LIMIT);
  }, [loadClosedSessions]);

  const clearChat = useCallback(() => {
    setMessages([]);
    setSuggestions([]);
    setRecommendation(null);
  }, []);

  const [isCurrentSessionNew, setIsCurrentSessionNew] = useState(false);
  const specialistBootstrapAttemptedRef = useRef(false);
  const prevEntrySessionIdRef = useRef<string | undefined>(undefined);

  /** When the active session row changes (e.g. sidebar "New Chat" closes prior session + creates a new id), wipe local transcript so UI matches the empty thread. */
  useEffect(() => {
    const sid = currentEntrySession?.id;
    const prev = prevEntrySessionIdRef.current;
    if (prev !== undefined && sid !== undefined && prev !== sid) {
      clearChat();
      setIsCurrentSessionNew(false);
      sessionStartTime.current = null;
      setIsDailyResetPolling(false);
      specialistBootstrapAttemptedRef.current = false;
    }
    prevEntrySessionIdRef.current = sid;
  }, [currentEntrySession?.id, clearChat]);

  useEffect(() => {
    specialistBootstrapAttemptedRef.current = false;
  }, [chatEntry]);

  // 1. try to load current session
  useEffect(() => {
    getSession(chatEntry, true);
  }, [chatEntry, getSession]);

  // 2. if current session exists, chat history starts loading
  const { chatHistory, isChatHistoryLoading } = useSessionHistory({
    sessionId: currentEntrySession?.id,
    enabled: !isCurrentSessionNew,
    pollingInterval: isDailyResetPolling ? DAILY_RESET_POLLING_INTERVAL : 0,
  });

  // 3. if chat history exists, initialize messages and suggestions — but never let a
  // momentarily empty/partial history (persist() still in flight) clobber a live transcript.
  useEffect(() => {
    const decision = resolveMessagesFromHistory({
      chatHistory,
      localMessages: messagesRef.current,
      isSendingMessage,
      chatEntry,
    });
    if (!decision.applied) return;
    setMessages(decision.messages);
    setSuggestions(decision.suggestions);
  }, [chatHistory, isSendingMessage, chatEntry]);

  // 4. if chat history does not exist, add initial message
  const rawSession = sessions?.[chatEntry];
  const sessionSlotReady =
    sessions !== null && Object.prototype.hasOwnProperty.call(sessions, chatEntry);
  const isInitialized =
    sessionSlotReady &&
    (rawSession === null || (rawSession !== undefined && !isChatHistoryLoading));
  const isNoChatHistory = isInitialized && !chatHistory?.length;

  const initializeAgentResetSession = useCallback(
    async (sessionType: Exclude<ChatEntry, "ai_mentor" | "get_coaching">) => {
      let session = sessions?.[sessionType] ?? null;
      if (!session) {
        session = await createNewSession(sessionType);
        if (!session) {
          specialistBootstrapAttemptedRef.current = false;
          return;
        }
        setIsCurrentSessionNew(true);
      }

      sessionStartTime.current = Date.now();
      trackChatSessionStart({
        chatType: "text",
        agentType: sessionType,
        sessionId: session.id,
      });

      const response = await sendMessage(sessionType, session, sessionType);
      const contentString = toAssistantBubbleHtml(assistantReplyHtmlFromWebhookResponse(response));
      setMessages((prev) => [...prev, createChatMessage("assistant", contentString)]);
      setSuggestions((response as ChatResponse | undefined)?.suggestions || []);
      setIsCurrentSessionNew(false);
      refreshConversationHistory();
    },
    [createNewSession, sendMessage, sessions, trackChatSessionStart, refreshConversationHistory]
  );

  useEffect(() => {
    if (!isInitialized || messages.length > 0) return;
    if (!isPrimaryChatEntry(chatEntry) && chatHistory && chatHistory.length > 0) return;

    if (isPrimaryChatEntry(chatEntry)) {
      if (!isNoChatHistory) return;
      if (hubLayout) return;
      let initialBody: string;
      if (chatEntry === "get_coaching" && teamsCoachingSeed) {
        initialBody = teamsCoachingSeedAssistantHtml(teamsCoachingSeed);
      } else {
        initialBody = chatEntry === "get_coaching" ? getCoachingInitialAssistantMessage(persona) : getHubInitialMessage(persona);
      }
      const initialMessage = createChatMessage("assistant", initialBody);
      setMessages([initialMessage]);
    } else if (!specialistBootstrapAttemptedRef.current) {
      specialistBootstrapAttemptedRef.current = true;
      void initializeAgentResetSession(chatEntry);
    }
  }, [
    isNoChatHistory,
    chatEntry,
    initializeAgentResetSession,
    messages,
    hubLayout,
    teamsCoachingSeed,
    persona,
    isInitialized,
    chatHistory,
  ]);

  useEffect(() => {
    if (isPrimaryChatEntry(chatEntry)) {
      if (isDailyResetPolling) setIsDailyResetPolling(false);
      return;
    }
    // Poll for async webhook history only while the thread is still empty — never block the UI after content appears.
    if (messages.length > 0 || (chatHistory && chatHistory.length > 1)) {
      if (isDailyResetPolling) setIsDailyResetPolling(false);
      return;
    }
    if (!isChatHistoryLoading && chatHistory && chatHistory.length <= 1) {
      setIsDailyResetPolling(true);
    }
  }, [chatEntry, isChatHistoryLoading, chatHistory, isDailyResetPolling, messages.length]);

  const handleSendMessage = async (content: string, _sessionFromInput?: UserSession | null) => {
    setMessages((prev) => [...prev, createChatMessage("user", content)]);

    if (isPrimaryChatEntry(chatEntry)) {
      const immediate = matchRecommendationFromUserMessage(content, { persona, seenIds });
      setRecommendation(immediate);
    } else {
      setRecommendation(null);
    }

    let session = currentEntrySession;
    if (!session && !isLoadingSession) {
      session = await createNewSession(chatEntry);
      setIsCurrentSessionNew(true);

      // Track chat session start for open-ended mentor-style entries
      if (isPrimaryChatEntry(chatEntry)) {
        sessionStartTime.current = Date.now();
        trackChatSessionStart({
          chatType: "text",
          agentType: chatEntry,
          sessionId: session?.id,
        });
      }
    }

    const placeholderId = Math.random().toString(36).substring(2, 9);
    setMessages((prev) => [...prev, createChatMessage("assistant", "", placeholderId)]);

    let chatResponse: ChatResponse | undefined;
    let rawStream = "";
    setOrganizationGuidanceStatus(null);

    try {
      for await (const event of sendMessageStream(content, session, chatEntry)) {
        if (event.type === "token") {
          rawStream += event.text;
          const html = toAssistantBubbleHtml(rawStream);
          setMessages((prev) => prev.map((m) => (m.id === placeholderId ? { ...m, content: html } : m)));
        } else if (event.type === "done") {
          chatResponse = event.response;
          const contentString = finalizeAssistantHtml(event.response, rawStream);
          setMessages((prev) => prev.map((m) => (m.id === placeholderId ? { ...m, content: contentString } : m)));
          setSuggestions(event.response?.suggestions || []);
        } else if (event.type === "error") {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === placeholderId
                ? { ...m, content: `<p class="text-sm text-stone-600">⚠️ ${event.message}</p>` }
                : m
            )
          );
        } else if (event.type === "organization_guidance_lookup_started") {
          setOrganizationGuidanceStatus("Checking your organization’s guidance…");
        } else if (
          event.type === "organization_guidance_lookup_completed" ||
          event.type === "organization_guidance_lookup_no_result" ||
          event.type === "organization_guidance_lookup_failed"
        ) {
          setOrganizationGuidanceStatus(null);
        }
      }
    } catch (error) {
      console.error("Streaming failed, falling back to non-streaming:", error);
      const response = await sendMessage(content, session, chatEntry);
      chatResponse = response as ChatResponse | undefined;
      const contentString = finalizeAssistantHtml(chatResponse, rawStream);
      setMessages((prev) => prev.map((m) => (m.id === placeholderId ? { ...m, content: contentString } : m)));
      setSuggestions(chatResponse?.suggestions || []);
    }

    setIsCurrentSessionNew(false);
    refreshConversationHistory();

    // Recommendation is only offered from the open-ended primary entries
    // (ai_mentor / get_coaching). Specialist chat spaces are themselves the
    // destination, so cross-suggesting away from them would be confusing.
    if (isPrimaryChatEntry(chatEntry)) {
      const resolved = resolveChatRecommendation({
        response: chatResponse,
        userMessage: content,
        persona,
        seenIds,
      });
      if (resolved) {
        setRecommendation((prev) => {
          if (prev?.source === "explicit_ask" && resolved.source !== "explicit_ask") {
            return prev;
          }
          return resolved;
        });
      }
    } else {
      setRecommendation(null);
    }
  };

  const handleSuggestionClick = (suggestion: Suggestion) => {
    const content = resolveQuickAnswerMessage(suggestion, inputDraftRef.current);
    inputDraftRef.current = "";
    setInputDraftClearKey((key) => key + 1);
    handleSendMessage(content);
  };

  const handleFilesSelected = useCallback(
    async (files: FileList) => {
      const file = files[0];
      if (!file) return;
      const result = await readChatTextAttachment(file);
      if (!result.ok) {
        if (result.code === "size") {
          toast({
            title: "File too large",
            description: "Please use a text file under 256 KB for now.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Unsupported file type",
            description: "Try a .txt or .md file, or describe the file in your message.",
          });
        }
        return;
      }
      await handleSendMessage(result.body);
    },
    [toast, handleSendMessage]
  );

  const showSuggestions = suggestions.length > 0 && !isSendingMessage;

  /** Hub primary chat before any messages: shorter card so empty space does not fill the viewport. */
  const compactEmptyHubPrimary =
    hubLayout && chatEntry === "ai_mentor" && messages.length === 0 && !isSendingMessage;

  const contentCardClass = cn(
    "flex flex-1 min-h-0 flex-col border-2 border-[#C58C10] px-0 md:px-0",
    compactEmptyHubPrimary ? "min-h-[200px] md:min-h-[240px]" : "min-h-0"
  );

  const onResetSessionClick = () => {
    // Track session end before clearing
    if (sessionStartTime.current) {
      const duration = Math.floor((Date.now() - sessionStartTime.current) / 1000);
      trackChatSessionEnd({
        chatType: "text",
        agentType: chatEntry,
        sessionId: currentEntrySession?.id,
        duration,
      });
      sessionStartTime.current = null;
    }

    clearChat();
    if (!isPrimaryChatEntry(chatEntry)) {
      specialistBootstrapAttemptedRef.current = false;
      void initializeAgentResetSession(chatEntry);
    }
  };

  const chatCard = (
    <ContentCard className={contentCardClass}>
      <ChatHistory
        className="mb-2 min-h-0 flex-1 overflow-y-auto"
        revisionKey={chatEntry}
        messages={messages}
        isLoading={isSendingMessage}
        userName={userData?.user_name?.trim() || "there"}
        isInitialization={!isInitialized || (isDailyResetPolling && messages.length === 0)}
      />
      {organizationGuidanceStatus && (
        <p className="px-4 text-xs text-stone-500" aria-live="polite">
          {organizationGuidanceStatus}
        </p>
      )}
      {isInitialized && showSuggestions && (
        <ChatQuickAnswers suggestions={suggestions} onSelectSuggestion={handleSuggestionClick} chatEntry={chatEntry} />
      )}
      {isInitialized && recommendation && !isSendingMessage && (
        <ChatRecommendationCard
          recommendation={recommendation.definition}
          reason={recommendation.reason}
          onDismiss={() => {
            // Once dismissed, suppress this destination on future prompts —
            // the user can still summon it by typing the literal name.
            markSeen(recommendation.definition.id);
            setRecommendation(null);
          }}
          onOpen={(rec) => {
            // Opening counts as "they know about it now" — also suppress
            // future auto-suggestions for this destination.
            markSeen(rec.id);
          }}
        />
      )}
      <ChatInput
        session={sessions?.[chatEntry]}
        onSubmit={handleSendMessage}
        isLoading={isSendingMessage}
        isDisabled={isSendingMessage || (!sessions?.[chatEntry] && isLoadingSession) || isDailyResetPolling}
        onVoiceClick={isVoiceModeAvailable && !suppressInlineVoice ? toggleInputMode : undefined}
        chatEntry={chatEntry}
        onFilesSelected={handleFilesSelected}
        placeholder={hubLayout ? "How can I help you today?" : undefined}
        initialDraft={mentorDraftMessage}
        onDraftChange={(draft) => {
          inputDraftRef.current = draft;
        }}
        draftClearKey={inputDraftClearKey}
      />
    </ContentCard>
  );

  /** Hub "New Chat" hero: show centered greeting + input + chips while the thread is empty; swap to the chat card on first message. */
  const showHubHero = hubHero && isInitialized && messages.length === 0 && !isSendingMessage;

  return (
    <>
      {showHubHero ? (
        <HubHero
          greetingLine={hubGreetingLine ?? "Welcome."}
          session={sessions?.[chatEntry] ?? null}
          isLoading={isSendingMessage}
          isDisabled={isSendingMessage || (!sessions?.[chatEntry] && isLoadingSession) || isDailyResetPolling}
          onSubmit={handleSendMessage}
          onVoiceClick={isVoiceModeAvailable ? toggleInputMode : undefined}
          onFilesSelected={handleFilesSelected}
        />
      ) : (
        chatCard
      )}

      {(!hubLayout || !showHubHero) && (
        <div className="mt-1 flex w-full shrink-0 flex-col gap-2 md:gap-2">
          {!hubLayout && (
            <SessionInfo
              session={sessions?.[chatEntry] || null}
              isLoading={isLoadingSession || isSendingMessage}
              onResetSession={onResetSessionClick}
              disabled={isLoadingSession || isSendingMessage || !isInitialized || isDailyResetPolling}
            />
          )}

          <AIMentorPageActionsFooter
            sessionId={currentEntrySession?.id}
            hasTranscript={messages.some((m) => m.role === "user")}
            isTranscriptLoading={Boolean(currentEntrySession?.id && isChatHistoryLoading)}
            transcriptLines={
              messages.length
                ? messages.map((m) => ({
                    role: m.role,
                    content: m.content,
                  }))
                : undefined
            }
          />
        </div>
      )}
    </>
  );
};

const createChatMessage = (role: "user" | "assistant", content: string, id?: string): ChatMessage => {
  return {
    id: id ?? Math.random().toString(36).substring(2, 9),
    role,
    content,
    timestamp: new Date(),
  };
};

/** Maps the edge-function ChatResponse (or a legacy webhook JSON shape) to HTML for the transcript. */
function assistantReplyHtmlFromWebhookResponse(response: ChatResponse | undefined): string {
  if (response === undefined) {
    return `<p class="text-sm text-stone-600">We couldn&rsquo;t reach the coach service. Check that <code class="rounded bg-stone-100 px-1 py-0.5 text-xs">VITE_WEBHOOK_URL</code> in <code class="rounded bg-stone-100 px-1 py-0.5 text-xs">.env</code> points to the educator-wellbeing edge function and restart Vite.</p>`;
  }

  const text = extractAssistantMessageText(response as unknown);
  if (text) return `${text}${organizationGuidanceCitationHtml(response)}`;

  return `<p class="text-sm text-stone-600">The coach returned a response we couldn&rsquo;t parse. In dev, check the browser console for logged JSON keys. Ensure the edge function returns assistant text as <code class="rounded bg-stone-100 px-1 py-0.5 text-xs">content.body</code> (HTML or plain text), <code class="rounded bg-stone-100 px-1 py-0.5 text-xs">agent_response</code>, or common fields such as <code class="rounded bg-stone-100 px-1 py-0.5 text-xs">output</code> / <code class="rounded bg-stone-100 px-1 py-0.5 text-xs">message</code>.</p>`;
}

function organizationGuidanceCitationHtml(response: ChatResponse): string {
  const citations = response.content?.citations;
  if (!citations?.length) return "";
  const labels = citations
    .filter((citation) => citation.title)
    .map((citation) => `${escapeHtml(citation.title ?? "")}${citation.locator ? ` — ${escapeHtml(citation.locator)}` : ""}`);
  return labels.length
    ? `<p class="mt-3 text-xs text-stone-500"><strong>Organization guidance:</strong> ${labels.join("; ")}</p>`
    : "";
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[character] ?? character);
}

/**
 * Resolves the final assistant bubble HTML once the stream is done. Prefers the
 * webhook response's extracted content; if that fell through to the generic
 * "couldn't reach/parse" fallback, keeps whatever was progressively streamed
 * (stripped of stream protocol) rather than wiping content the user already saw.
 */
function finalizeAssistantHtml(response: ChatResponse | undefined, streamedRaw: string): string {
  const fromDone = assistantReplyHtmlFromWebhookResponse(response);
  const isErrorFallback =
    !fromDone ||
    fromDone.includes("couldn&rsquo;t reach the coach") ||
    fromDone.includes("couldn&rsquo;t parse");
  if (!isErrorFallback) {
    return toAssistantBubbleHtml(fromDone);
  }
  const progressive = toAssistantBubbleHtml(streamedRaw);
  if (progressive.trim()) return progressive;
  return fromDone;
}
